/**
 * VideoProgressBar
 *
 * Self-contained progress bar component for video playback.
 * Features two overlapped progress bars:
 * 1. Inactive background bar showing two colored sections (before/after typeLimit)
 * 2. Active progress bar showing current playback position in white
 *
 * Interaction is handled by an invisible range input overlay.
 *
 * Performance:
 * - Single useEffect handles both initial render and continuous updates
 * - Gets video element from registry once on mount and stores in ref
 * - Sets initial progress immediately with forceUpdate flag (works for paused videos and fullscreen)
 * - Updates DOM directly via refs without triggering parent re-renders
 * - Update interval: 100ms with matching CSS transition for responsive seeking
 * - No React state changes = no cascade of re-renders up the component tree
 */

import React, { useRef, useEffect } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';

export interface VideoProgressBarProps {
  /** Video key to identify which video to track */
  videoKey: string;

  /** Total duration in seconds */
  duration: number;

  /** Type limit in seconds - splits the progress bar into two colored sections */
  typeLimit?: number | null;

  /** Callback to seek to specific time */
  onSeek?: (time: number) => void;

  /** Callback when user starts dragging */
  onDragStart?: () => void;

  /** Callback when user stops dragging */
  onDragEnd?: () => void;

  /** Whether the video element is ready in the registry - starts the animation loop */
  isVideoReady?: boolean;

  /** Custom class name */
  className?: string;
}

export const VideoProgressBar: React.FC<VideoProgressBarProps> = ({
  videoKey,
  duration,
  typeLimit,
  onSeek,
  onDragStart,
  onDragEnd,
  isVideoReady = false,
  className = ''
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const progressBarRef = useRef<HTMLDivElement>(null);
  const rangeInputRef = useRef<HTMLInputElement>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  // Calculate type limit percentage (if provided)
  const typeLimitPercent = duration > 0 && typeLimit ? (typeLimit / duration) * 100 : 0;

  // Self-contained progress update loop - starts once isVideoReady is true
  useEffect(() => {
    if (!isVideoReady || !progressBarRef.current || !rangeInputRef.current || duration <= 0) return;

    const videoElement = videoElementRegistry.get(videoKey);
    if (!videoElement) return;

    videoElementRef.current = videoElement;

    let animationFrameId: number;
    let lastUpdateTime = 0;
    const UPDATE_INTERVAL = 100; // Update every 100ms for smooth animation

    const updateProgress = (timestamp: number, forceUpdate = false) => {
      const video = videoElementRef.current;
      if (!video) return;

      // Update if forced (initial render) or if playing and enough time has passed
      const shouldUpdate = forceUpdate ||
        (!video.paused && timestamp - lastUpdateTime >= UPDATE_INTERVAL);

      if (shouldUpdate) {
        const currentTime = video.currentTime;
        const progressPercent = (currentTime / duration) * 100;

        // Update progress bar width via DOM
        if (progressBarRef.current) {
          progressBarRef.current.style.width = `${progressPercent}%`;
        }

        // Update range input value (for dragging)
        if (rangeInputRef.current) {
          rangeInputRef.current.value = currentTime.toString();
        }

        lastUpdateTime = timestamp;
      }

      // Only keep the loop running while playing; restart it on the play event
      if (!video.paused) {
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    const startLoop = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(updateProgress);
    };

    videoElement.addEventListener('play', startLoop);

    // Set initial progress immediately (important for paused videos and fullscreen)
    updateProgress(0, true);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      videoElement.removeEventListener('play', startLoop);
    };
  }, [isVideoReady, videoKey, duration]);

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    onSeek?.(newTime);
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="relative" style={{ height: `${responsive.progressBar.height}px` }}>
        {/* Inactive background progress bar - shows two colored sections based on typeLimit */}
        {typeLimit && typeLimit > 0 ? (
          <div className="absolute inset-0 flex rounded-full overflow-hidden" style={{ height: `${responsive.progressBar.height}px` }}>
            {/* Section before typeLimit - disabled color */}
            <div
              style={{
                height: `${responsive.progressBar.height}px`,
                width: `${typeLimitPercent}%`,
                backgroundColor: theme.white
              }}
            />
            {/* Section after typeLimit - exclusive color */}
            <div
              className="flex-1"
              style={{
                height: `${responsive.progressBar.height}px`,
                backgroundColor: theme.exclusive
              }}
            />
          </div>
        ) : (
          /* Fallback - single color background if no typeLimit */
          <div
            className="absolute inset-0 rounded-full"
            style={{
              height: `${responsive.progressBar.height}px`,
              backgroundColor: theme.disabled
            }}
          />
        )}

        {/* Active progress bar - shows current playback progress in white */}
        <div
          ref={progressBarRef}
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            height: `${responsive.progressBar.height}px`,
            width: '0%', // Initial width, updated via ref in useEffect
            backgroundColor: theme.activeRed,
            // Smooth interpolated animation - updates every 100ms with matching transition
            // Transition duration matches UPDATE_INTERVAL for responsive seeking
            transition: 'width 100ms linear',
            willChange: 'width'
          }}
        />

        {/* Progress input (invisible but handles interaction) */}
        <input
          ref={rangeInputRef}
          type="range"
          min={0}
          max={duration || 0}
          defaultValue={0}
          onChange={handleProgressChange}
          onMouseDown={onDragStart}
          onMouseUp={onDragEnd}
          onTouchStart={onDragStart}
          onTouchEnd={onDragEnd}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          style={{ height: `${responsive.progressBar.height}px`, zIndex: 10 }}
        />
      </div>
    </div>
  );
};
